import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Cpu, Briefcase, GraduationCap, Monitor, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useLanguage } from '../context/LanguageContext';

const ServicesSection = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const iconMap = {
    users: Users,
    cpu: Cpu,
    briefcase: Briefcase,
    graduationCap: GraduationCap,
    monitor: Monitor
  };

  const services = [
    {
      id: 1,
      slug: 'ai-tabanli-secme-yerlestirme',
      icon: 'users'
    },
    {
      id: 2,
      slug: 'ik-sistem-kurulum',
      icon: 'cpu'
    },
    {
      id: 3,
      slug: 'proje-bazli-danismanlik',
      icon: 'briefcase'
    },
    {
      id: 4,
      slug: 'dijital-ik-egitimi',
      icon: 'graduationCap'
    },
    {
      id: 5,
      slug: 'kurumsal-yazilim-cozumleri',
      icon: 'monitor'
    }
  ];

  return (
    <section id="services" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-yellow-400 text-gray-900 rounded-full text-sm font-bold mb-4">
            {t('services.badge')}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('services.title')}
            <br />
            <span className="text-yellow-500">
              {t('services.titleHighlight')}
            </span>
          </h2>
        </div>

        {/* Services Grid - 5 Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card
                key={service.id}
                onClick={() => navigate(`/hizmet/${service.slug}`)}
                className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group bg-white overflow-hidden cursor-pointer"
              >
                <div className="h-2 bg-yellow-400"></div>
                <CardHeader>
                  <div className="w-16 h-16 rounded-2xl bg-yellow-400 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="h-8 w-8 text-gray-900" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 mb-3">
                    {t(`services.service${service.id}.title`)}
                  </CardTitle>
                  <div className="text-sm font-bold text-yellow-600 mb-3">
                    {t(`services.service${service.id}.subtitle`)}
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 leading-relaxed mb-4">
                    {t(`services.service${service.id}.description`)}
                  </CardDescription>
                  
                  {/* Features List */}
                  <ul className="space-y-2 mb-4">
                    {[0, 1, 2, 3].map((index) => {
                      const featureKey = `services.service${service.id}.features.${index}`;
                      const feature = t(featureKey);
                      // Only show if translation exists (not showing the key itself)
                      if (feature && !feature.includes('services.service')) {
                        return (
                          <li key={index} className="text-sm text-gray-600 flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            <span>{feature}</span>
                          </li>
                        );
                      }
                      return null;
                    })}
                  </ul>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 font-bold text-sm group-hover:text-yellow-600 transition-colors">
                      {t('services.detailInfo')} →
                    </span>
                    <ArrowRight className="h-5 w-5 text-yellow-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;